import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { type ColumnDef } from "@tanstack/react-table";

import { DataTable } from "./data-table";

import type { ListaZplItem } from "@/types/lista-zpl-item";
import inovaone_api from "@/services/inovaone_api";
import { alert } from "@/contexts/AlertModalContext";
import { useAuth } from "@/hooks/useAuth";
import { useGlobalLoader } from "./GlobalLoader";
import { useImpressaoConfig } from "@/contexts/ImpressaoConfigContext";

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { converterListaZplItemParaUrl } from "@/utils/UtilFuncoes";
import { dpmmDe, renderizarPreview } from "@/utils/zplDesigner";
import { preencherVariaveisExemplo } from "@/utils/variaveisZpl";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Skeleton } from "./ui/skeleton";
import { DialogoAlerta } from "./alert-dialog";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import {
  ExternalLink,
  Wand2,
  Eye,
  Pencil,
  Plus,
  Ruler,
  Star,
  Tag,
  Tags,
  Trash2,
} from "lucide-react";
import { AdminContext } from "@/contexts/AdminContext";

type EtiquetasComponentContextType = {};

const EtiquetasComponentContext = createContext<
  EtiquetasComponentContextType | undefined
>(undefined);

export function EtiquetasComponentProvider() {
  return (
    <EtiquetasComponentContext.Provider value={{}}>
      <EtiquetasComponent />
    </EtiquetasComponentContext.Provider>
  );
}

export function useEtiquetasComponent() {
  const context = useContext(EtiquetasComponentContext);
  if (!context) {
    throw new Error(
      "useEtiquetasComponent deve ser usado dentro de EtiquetasComponentProvider",
    );
  }
  return context;
}


/** Renderiza a etiqueta via POST — o GET estoura o limite de URL com logos. */
function PreviewEtiqueta({ item }: { item: ListaZplItem }) {
  const [url, setUrl] = useState<string | null>(null);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    const controlador = new AbortController();
    let objectUrl: string | null = null;
    renderizarPreview(
      preencherVariaveisExemplo(item.zpl || ""),
      {
        larguraMm: Number(item.largura) || 100,
        alturaMm: Number(item.altura) || 50,
        dpmm: dpmmDe(item.dpmm || "203"),
      },
      controlador.signal,
    ).then((r) => {
      if (controlador.signal.aborted) return;
      if (r.ok) {
        objectUrl = r.url;
        setUrl(r.url);
      } else if (r.erro) {
        setErro(true);
      }
    });
    return () => {
      controlador.abort();
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [item.zpl, item.largura, item.altura, item.dpmm]);

  if (erro)
    return (
      <div className="flex h-32 items-center justify-center rounded bg-muted text-xs text-muted-foreground">
        Não foi possível renderizar — verifique o ZPL.
      </div>
    );
  if (!url)
    return <Skeleton className="h-32 w-full rounded" />;
  return (
    <img src={url} alt={item.nome} className="w-full h-auto bg-white rounded" />
  );
}

function EtiquetasComponent() {
  const context = useContext(EtiquetasComponentContext);
  if (!context) return null;
  const { isLoading, setIsLoading } = useGlobalLoader();
  const { requireAdmin } = useContext(AdminContext)!;
  const { usuario } = useAuth();
  const { impressaoConfig, getImpressaoConfig } = useImpressaoConfig();
  const navigate = useNavigate();

  const [etiquetas, setEtiquetas] = useState<ListaZplItem[]>([]);
  const [etiquetaParaApagar, setEtiquetaParaApagar] =
    useState<ListaZplItem | null>(null);
  const [alertOpen, setAlertOpen] = useState(false);

  // ---- Etiqueta padrão derivada ----
  const defaultEtiqueta = useMemo(() => {
    if (!impressaoConfig?.lista_zpl) return null;
    const idx = parseInt(impressaoConfig.etiqueta_padrao || "0");
    return impressaoConfig.lista_zpl[idx] || null;
  }, [impressaoConfig?.lista_zpl, impressaoConfig?.etiqueta_padrao]);

  // ---- Actions ----
  const validarUsuario = () => {
    if (!impressaoConfig?._id) {
      alert.error("Configuração de impressão não encontrada.");
      return false;
    }
    if (!usuario?._id || !usuario?.id_conta) {
      alert.error("Usuário inválido.");
      return false;
    }
    return true;
  };

  const tornarEtiquetaPadrao = async (item: ListaZplItem) => {
    if (!validarUsuario() || !impressaoConfig) return;
    setIsLoading(true);
    try {
      const idx = impressaoConfig.lista_zpl.findIndex(
        (z) => z.nome === item.nome,
      );
      if (idx < 0) throw new Error("Etiqueta não encontrada.");
      await inovaone_api.post(`/impressao_config`, [
        { ...impressaoConfig, etiqueta_padrao: String(idx) },
      ]);
      await getImpressaoConfig();
      alert.success("Etiqueta padrão atualizada.");
    } catch {
      alert.error("Erro ao tornar etiqueta padrão.");
    } finally {
      setIsLoading(false);
    }
  };

  const apagarEtiqueta = async (item: ListaZplItem) => {
    if (!validarUsuario() || !impressaoConfig) return;
    setIsLoading(true);
    try {
      await inovaone_api.post(`/impressao_config`, [
        {
          ...impressaoConfig,
          lista_zpl: impressaoConfig.lista_zpl.filter(
            (z) => z.nome !== item.nome,
          ),
        },
      ]);
      await getImpressaoConfig();
      alert.success("Etiqueta apagada.");
    } catch {
      alert.error("Erro ao apagar etiqueta.");
    } finally {
      setIsLoading(false);
    }
  };

  const abrirEditor = (item?: ListaZplItem) => {
    if (!item) {
      navigate("/etiquetas/editor");
      return;
    }
    requireAdmin(() =>
      navigate(`/etiquetas/editor?nome=${encodeURIComponent(item.nome)}`),
    );
  };

  // ---- Effects ----
  useEffect(() => {
    if (impressaoConfig?.lista_zpl) {
      const padrao = defaultEtiqueta;
      if (padrao) {
        setEtiquetas([
          padrao,
          ...impressaoConfig.lista_zpl.filter((z) => z.nome !== padrao.nome),
        ]);
      } else {
        setEtiquetas(impressaoConfig.lista_zpl);
      }
    }
  }, [impressaoConfig, defaultEtiqueta]);

  // ---- Columns ----
  const columns: ColumnDef<ListaZplItem>[] = [
    {
      id: "preview",
      header: "Pré-visualização",
      cell: ({ row }) => {
        const item = row.original;
        const url = converterListaZplItemParaUrl(item);
        return (
          <HoverCard openDelay={150} closeDelay={50}>
            <HoverCardTrigger asChild>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs text-primary hover:underline cursor-pointer"
              >
                <Eye className="h-3.5 w-3.5" />
                Pré-visualizar
              </a>
            </HoverCardTrigger>
            <HoverCardContent className="w-72 p-2">
              <PreviewEtiqueta item={item} />
              {url.length < 4000 && (
                <a
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 flex items-center justify-center gap-1 text-xs text-primary hover:underline"
                >
                  <ExternalLink className="h-3 w-3" />
                  Abrir em nova aba
                </a>
              )}
            </HoverCardContent>
          </HoverCard>
        );
      },
    },
    {
      accessorKey: "nome",
      header: "Descrição",
      cell: ({ row }) => {
        const item = row.original;
        const isDefault = defaultEtiqueta?.nome === item.nome;
        return (
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-medium truncate">{item.nome}</span>
            {isDefault && (
              <Badge
                variant="outline"
                className="bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20 gap-1"
              >
                <Star className="h-3 w-3 fill-amber-500 text-amber-500" />
                Padrão
              </Badge>
            )}
            {item.design && !item.zpl_manual && (
              <Badge variant="secondary" className="font-normal gap-1">
                <Wand2 className="h-3 w-3" />
                Visual
              </Badge>
            )}
          </div>
        );
      },
    },
    {
      id: "tamanho",
      header: "Tamanho",
      cell: ({ row }) => {
        const item = row.original;
        return (
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {item.largura} × {item.altura} mm
          </span>
        );
      },
    },
    {
      accessorKey: "dpmm",
      header: "DPI",
      cell: ({ row }) => (
        <Badge variant="secondary" className="font-normal">
          {row.original.dpmm}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: () => <div className="text-right">Ações</div>,
      cell: ({ row }) => {
        const item = row.original;
        const isDefault = defaultEtiqueta?.nome === item.nome;
        return (
          <TooltipProvider delayDuration={250}>
            <div className="flex items-center gap-1 justify-end">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    disabled={isLoading || isDefault}
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => tornarEtiquetaPadrao(item)}
                    aria-label="Tornar padrão"
                  >
                    <Star
                      className={`h-4 w-4 ${
                        isDefault
                          ? "fill-amber-500 text-amber-500"
                          : "text-muted-foreground"
                      }`}
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {isDefault ? "Já é a etiqueta padrão" : "Tornar padrão"}
                </TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    onClick={() => abrirEditor(item)}
                    aria-label="Editar"
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Editar</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    disabled={isLoading || isDefault}
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-500/10 disabled:text-muted-foreground"
                    onClick={() => {
                      requireAdmin(() => {
                        setEtiquetaParaApagar(item);
                        setAlertOpen(true);
                      });
                    }}
                    aria-label="Apagar"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {isDefault ? "Não é possível apagar a padrão" : "Apagar"}
                </TooltipContent>
              </Tooltip>
            </div>
          </TooltipProvider>
        );
      },
    },
  ];

  const tamanhoPadrao = defaultEtiqueta
    ? `${defaultEtiqueta.largura} × ${defaultEtiqueta.altura} mm`
    : "—";

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <h2 className="text-xl font-semibold tracking-tight">Etiquetas</h2>
          <p className="text-xs text-muted-foreground mt-0.5">
            Gerencie os modelos ZPL utilizados na impressão
          </p>
        </div>
        <Button size="sm" onClick={() => abrirEditor()}>
          <Plus className="h-4 w-4" />
          <span>Nova etiqueta</span>
        </Button>
      </div>

      {/* Stats - 3 cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Total de etiquetas
              </CardTitle>
              <Tags className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="text-2xl font-bold">{etiquetas.length}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {etiquetas.length === 1
                ? "modelo cadastrado"
                : "modelos cadastrados"}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Etiqueta padrão
              </CardTitle>
              <Star className="h-4 w-4 fill-amber-500 text-amber-500" />
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div
              className="text-base font-semibold truncate"
              title={defaultEtiqueta?.nome}
            >
              {defaultEtiqueta?.nome || "—"}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {defaultEtiqueta ? "selecionada" : "nenhuma definida"}
            </p>
          </CardContent>
        </Card>

        <Card className="hover:shadow-md transition-shadow">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                Tamanho da padrão
              </CardTitle>
              <Ruler className="h-4 w-4 text-muted-foreground" />
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="text-base font-semibold">
              {tamanhoPadrao}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {defaultEtiqueta?.dpmm ? `${defaultEtiqueta.dpmm} DPI` : "—"}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      {etiquetas.length === 0 ? (
        <Card>
          <CardContent className="py-16 flex flex-col items-center justify-center gap-3 text-muted-foreground">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted">
              <Tag className="h-6 w-6" />
            </div>
            <div className="text-center">
              <p className="text-sm font-medium text-foreground">
                Nenhuma etiqueta cadastrada
              </p>
              <p className="text-xs">
                Crie sua primeira etiqueta ZPL para começar a imprimir.
              </p>
            </div>
            <Button size="sm" onClick={() => abrirEditor()}>
              <Plus className="h-4 w-4" />
              Criar primeira etiqueta
            </Button>
          </CardContent>
        </Card>
      ) : (
        <DataTable columns={columns} data={etiquetas} />
      )}

      {/* Confirmação de exclusão */}
      <DialogoAlerta
        description="Tem certeza que deseja apagar esta etiqueta? Esta ação não pode ser desfeita."
        title="Apagar etiqueta"
        isOpen={alertOpen}
        onConfirm={() => {
          if (etiquetaParaApagar) apagarEtiqueta(etiquetaParaApagar);
        }}
        onOpenChange={() => {
          setAlertOpen(false);
          setEtiquetaParaApagar(null);
        }}
        key="dialogo-alerta-apagar-etiqueta"
      />
    </div>
  );
}
